import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import { useEffect, useState, useMemo } from 'react';
import Icon from 'src/@core/components/icon';
import { useLCSLHPTITablaValorHistorico } from '../hooks/useLCSLHPTITablaValorHistorico';
import { OperacionLCSLHPTITablaValorHistoricoFields } from '../types/fields';
import { OperacionLCSLHPTITablaValorHistoricoGrupo } from '../types/dto';
import { pivotLCSLHPTIData } from '../hooks/useLCSLHPTITablaValor';
import LCLSHPTITablaValores from './LCLSHPTITablaValores';

interface LCSLHPTITablaValoresHistorialDialogProps {
  idOperacion: number;
  open: boolean;
  onClose: () => void;
}

const VersionHistoricaItem = ({ version, expanded, onChange }: {
  version: OperacionLCSLHPTITablaValorHistoricoGrupo,
  expanded: boolean,
  onChange: (isExp: boolean) => void
}) => {

  const { groupedHeaders, rows } = useMemo(() => {
    const celdas = version[OperacionLCSLHPTITablaValorHistoricoFields.Celdas] || [];

    return pivotLCSLHPTIData(celdas);
  }, [version]);

  const fecha = version[OperacionLCSLHPTITablaValorHistoricoFields.FechaModificacion];
  const usuario = version[OperacionLCSLHPTITablaValorHistoricoFields.NombreUsuarioModificacion];
  const obs = version[OperacionLCSLHPTITablaValorHistoricoFields.Observaciones];

  return (
    <Accordion expanded={expanded} onChange={(_, isExpanded) => onChange(isExpanded)}>
      <AccordionSummary expandIcon={<Icon icon='mdi:chevron-down' />}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', pr: 4, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 'bold' }}>
              {fecha.toLocaleString()}
            </Typography>
            <Chip label={usuario} size='small' variant='outlined' color='primary' />
          </Box>
          {obs && (
            <Typography variant='caption' sx={{ fontStyle: 'italic', color: 'text.secondary', maxWidth: '40%' }} noWrap>
              {obs}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {obs && (
          <Box sx={{ mb: 4, p: 3, bgcolor: 'action.hover', borderRadius: 1, borderLeft: '4px solid #ccc' }}>
            <Typography variant='subtitle2' sx={{ mb: 1 }}>Observaciones del cambio:</Typography>
            <Typography variant='body2'>{obs}</Typography>
          </Box>
        )}
        <LCLSHPTITablaValores headers={groupedHeaders} rows={rows} />
      </AccordionDetails>
    </Accordion>
  );
};

export const LCSLHPTITablaValoresHistorialDialog = ({ idOperacion, open, onClose }: LCSLHPTITablaValoresHistorialDialogProps) => {
  const { versiones, isLoading } = useLCSLHPTITablaValorHistorico(idOperacion);
  const [expandedVersion, setExpandedVersion] = useState<string | false>(false);

  useEffect(() => {
    if (versiones.length > 0 && !expandedVersion) {
      setExpandedVersion(versiones[0][OperacionLCSLHPTITablaValorHistoricoFields.FechaModificacion].toISOString());
    }
  }, [versiones, expandedVersion]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Icon icon="mdi:history" />
        Historial de Tabla de Valores
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}><CircularProgress /></Box>
        ) : versiones.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No hay registros históricos para esta operación.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {versiones.map((v) => {
              const id = v[OperacionLCSLHPTITablaValorHistoricoFields.FechaModificacion].toISOString();

              return (
                <VersionHistoricaItem
                  key={id}
                  version={v}
                  expanded={expandedVersion === id}
                  onChange={(isExpanded) => setExpandedVersion(isExpanded ? id : false)}
                />
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};